import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { login, envoyerLienMotDePasse } from "@/lib/firebase/auth";
import { useAuth } from "@/lib/hooks/useAuth";

export default function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [oubli, setOubli] = useState(false);
  const [emailOubli, setEmailOubli] = useState("");
  const [envoye, setEnvoye] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Rediriger si déjà connecté
  if (user) {
    navigate("/");
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const authUser = await login(username, password);
      
      // Redirection selon le rôle
      if (authUser.role === "ADMIN") {
        navigate("/admin/dashboard");
      } else if (authUser.role === "COLLABORATEUR") {
        navigate("/collaborateur/dashboard");
      } else if (authUser.role === "FOURNISSEUR") {
        navigate("/fournisseur/dashboard");
      } else if (authUser.role === "SUPPORT") {
        navigate("/support/pilotage");
      }
    } catch (err: any) {
      // Firebase renvoie ses messages en anglais et en jargon
      // (« auth/invalid-credential »). On les traduit en une phrase, et on
      // ne distingue PAS l'identifiant inconnu du mot de passe faux : le
      // dire permettrait de decouvrir quels comptes existent en essayant
      // des identifiants au hasard.
      const brut = String(err?.code || err?.message || "");
      const identification =
        /auth\/(invalid-credential|wrong-password|user-not-found|invalid-email)/.test(
          brut,
        ) || err?.message === "Nom d'utilisateur ou mot de passe incorrect";

      if (identification) {
        setError("Identifiant ou mot de passe incorrect.");
      } else if (/auth\/too-many-requests/.test(brut)) {
        setError(
          "Trop de tentatives. Attends quelques minutes avant de réessayer.",
        );
      } else if (/auth\/network-request-failed|Failed to fetch/.test(brut)) {
        setError("Connexion au serveur impossible. Vérifie ta connexion.");
      } else {
        setError(err?.message || "Erreur de connexion.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Natura Beldi
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Connectez-vous à votre compte
          </p>
        </div>
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="username" className="sr-only">
                Nom d'utilisateur
              </label>
              <input
                id="username"
                name="username"
                type="text"
                autoComplete="username"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Nom d'utilisateur"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">
                Mot de passe
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="current-password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px]"
            >
              {loading ? "Connexion..." : "Se connecter"}
            </button>
          </div>

          {/* Mot de passe oublie.
              Le message de confirmation est toujours le meme, que l'adresse
              existe ou non : sinon cet ecran deviendrait un moyen de
              deviner qui travaille ici. */}
          <div className="text-center">
            {!oubli ? (
              <button
                type="button"
                onClick={() => setOubli(true)}
                className="text-sm text-primary hover:underline"
              >
                Mot de passe oublié ?
              </button>
            ) : envoye ? (
              <p className="text-sm text-gray-600">
                Si un compte existe avec cette adresse, un e-mail vient d'y
                être envoyé. Pensez à regarder vos spams.
              </p>
            ) : (
              <div className="space-y-2">
                <input
                  type="email"
                  value={emailOubli}
                  onChange={(e) => setEmailOubli(e.target.value)}
                  placeholder="Votre adresse e-mail"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                />
                <button
                  type="button"
                  disabled={!emailOubli}
                  onClick={async () => {
                    await envoyerLienMotDePasse(emailOubli);
                    setEnvoye(true);
                  }}
                  className="w-full py-2 text-sm font-medium text-primary border border-primary rounded-md hover:bg-primary/5 disabled:opacity-50"
                >
                  Recevoir un lien
                </button>
              </div>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

